"use client";

import type { ReactNode } from "react";
import { usePathname } from "next/navigation";
import { isExamInfoPage } from "@/lib/route-layout";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { GamjaPlatformProviders } from "@workspace/gamja-platform/components/providers";
import { ProblemDataAdapterProvider } from "@workspace/gamja-platform/components/problem-data-adapter";
import { ProblemAnswerSlotsProvider } from "@workspace/gamja-platform/components/problem-answer-slots";

import {
  SettingsProvider,
  SettingsHost,
} from "@/components/settings/SettingsProvider";
import { ExternalLearningSettingsContext } from "@workspace/gamja-platform/components/learning-settings-context";

import { ProblemCacheInvalidator } from "@/components/ProblemCacheInvalidator";
import { listJcgExamSessions } from "@/lib/exam-sessions-client-adapter";
import { jcgProblemDataAdapter } from "@/lib/problem-data-adapter";
import {
  jcgAdFreeProblemAnswerSlots,
  jcgProblemAnswerSlots,
} from "@/components/JcgProblemAnswerSlots";

/**
 * JCG 전용 provider 어댑터.
 *
 * 공용 provider 스택에 JCG 고유의 회차 조회·문제 캐시 동기화·devtools 를 주입한다.
 * 이 파일이 "use client" 인 이유: 래퍼 함수 prop 은 서버 컴포넌트에서 클라이언트로
 * 직렬화할 수 없으므로, 클라이언트 경계 안에서 만들어 넘겨야 한다.
 */
export function JcgPlatformProviders({
  children,
  afterQuery,
}: {
  children: ReactNode;
  afterQuery?: ReactNode;
}) {
  const pathname = usePathname();
  const problemAnswerSlots = isExamInfoPage(pathname)
    ? jcgAdFreeProblemAnswerSlots
    : jcgProblemAnswerSlots;

  return (
    <SettingsProvider>
      <GamjaPlatformProviders
        listExamSessions={listJcgExamSessions}
        queryWrapper={(inner) => (
          <ProblemCacheInvalidator>{inner}</ProblemCacheInvalidator>
        )}
        // React Query Devtools 플로팅 버튼(우하단 야자수)은 모바일 하단 dock 의 마지막 버튼과 겹치므로
        // 기본 숨김. 필요할 때만 `.env.local` 에 NEXT_PUBLIC_REACT_QUERY_DEVTOOLS=1 을 두고 dev 서버를
        // 다시 띄운다(NEXT_PUBLIC_* 는 빌드 시점 인라인). 운영 빌드에서는 패키지가 자체적으로 빠진다.
        queryExtras={
          process.env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS === "1" ? (
            <ReactQueryDevtools initialIsOpen={false} />
          ) : null
        }
        afterQuery={afterQuery}
      >
        <ProblemDataAdapterProvider adapter={jcgProblemDataAdapter}>
          <ProblemAnswerSlotsProvider slots={problemAnswerSlots}>
            <ExternalLearningSettingsContext.Provider value={true}>
              {children}
              <SettingsHost />
            </ExternalLearningSettingsContext.Provider>
          </ProblemAnswerSlotsProvider>
        </ProblemDataAdapterProvider>
      </GamjaPlatformProviders>
    </SettingsProvider>
  );
}
